import { useState, useEffect, useRef, useCallback } from "react";
import {
  adminPollSessions,
  adminGetSessionMessages,
  adminAssignSession,
  adminSendMessage,
  adminCloseSession,
  adminGetClosedSessions,
  adminGetCustomers,
  adminAddCustomer,
  adminUpdateCustomer,
  SessionRow,
  ChatMessage,
  CustomerRow,
} from "@/utils/chatApi";
import { userStore } from "@/utils/userStore";
import type { UserRole } from "@/utils/userStore";

// Bildirim sesi (lokal)
function playAdminNotification() {
  try {
    const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    [0, 0.12].forEach(t => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.connect(g); g.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.value = 880;
      g.gain.setValueAtTime(0.25, ctx.currentTime + t);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.3);
      osc.start(ctx.currentTime + t);
      osc.stop(ctx.currentTime + t + 0.3);
    });
  } catch { /* sessizce */ }
}

function relTime(iso: string): string {
  try {
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return "az once";
    if (diff < 3600) return `${Math.floor(diff / 60)}dk once`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}sa once`;
    return new Date(iso).toLocaleDateString("tr-TR");
  } catch { return ""; }
}

// ════════════════════════════════════════════
// Aktif Konusmalar Paneli
// ════════════════════════════════════════════

function ActiveChatsPanel() {
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionInfo, setSessionInfo] = useState<SessionRow | null>(null);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [inputMsg, setInputMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [closingId, setClosingId] = useState<string | null>(null);
  const [prevUnread, setPrevUnread] = useState<Record<string, number>>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const msgPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const session = userStore.getSession();
  const agentName = session?.displayName ?? "Yetkili";

  const scrollBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

  const pollSessions = useCallback(async () => {
    try {
      const res = await adminPollSessions();
      setSessions(res.sessions);
      setPrevUnread(prev => {
        res.sessions.forEach(s => {
          if (s.unread_admin > 0 && (prev[s.id] ?? 0) < s.unread_admin) {
            playAdminNotification();
          }
        });
        const next: Record<string, number> = {};
        res.sessions.forEach(s => { next[s.id] = s.unread_admin; });
        return next;
      });
    } catch { /* sessizce */ }
  }, []);

  useEffect(() => {
    pollSessions();
    pollRef.current = setInterval(pollSessions, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [pollSessions]);

  const pollMessages = useCallback(async (sid: string) => {
    try {
      const res = await adminGetSessionMessages(sid);
      setMessages(res.messages);
      setSessionInfo(res.session);
    } catch { /* sessizce */ }
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoadingMsgs(true);
    adminGetSessionMessages(selected).then(res => {
      setMessages(res.messages);
      setSessionInfo(res.session);
      setLoadingMsgs(false);
    }).catch(() => setLoadingMsgs(false));
    msgPollRef.current = setInterval(() => pollMessages(selected), 2000);
    return () => { if (msgPollRef.current) clearInterval(msgPollRef.current); };
  }, [selected, pollMessages]);

  useEffect(() => { scrollBottom(); }, [messages]);

  const handleAssign = async (sid: string) => {
    try { await adminAssignSession(sid, agentName); setSelected(sid); pollSessions(); } catch { /* hata */ }
  };

  const handleSend = async () => {
    const txt = inputMsg.trim();
    if (!txt || !selected) return;
    setInputMsg(""); setSending(true);
    try { await adminSendMessage(selected, txt, agentName); await pollMessages(selected); } catch { /* hata */ }
    setSending(false);
  };

  const handleClose = async (sid: string) => {
    setClosingId(sid);
    try {
      await adminCloseSession(sid);
      if (selected === sid) { setSelected(null); setMessages([]); setSessionInfo(null); }
      pollSessions();
    } catch { /* hata */ }
    setClosingId(null);
  };

  const statusBadge = (s: SessionRow) => {
    if (s.status === "waiting") return <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30 whitespace-nowrap">Bekliyor</span>;
    if (s.status === "active") return <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 whitespace-nowrap">Aktif</span>;
    return <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-[#7B8FAF] border border-white/10 whitespace-nowrap">Kapali</span>;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 h-[calc(100vh-260px)]">
      <div className="lg:col-span-2 flex flex-col gap-2 overflow-y-auto pr-1">
        {sessions.length === 0 ? (
          <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-10 text-center">
            <i className="ri-customer-service-2-line text-4xl text-[#5E7398]"></i>
            <p className="text-[#7B8FAF] text-sm mt-3">Aktif sohbet yok</p>
            <p className="text-[#465886] text-xs mt-1">Ziyaretci baglandiginda burada gorunur</p>
          </div>
        ) : sessions.map(s => (
          <div key={s.id} onClick={() => setSelected(s.id)}
            className={`bg-[#141E35] border rounded-xl p-4 cursor-pointer transition-all ${
              selected === s.id ? "border-[#3B82F6] bg-[#3B82F6]/10" : "border-[#2B3654] hover:border-[#3B82F6]/40"
            }`}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 rounded-full bg-[#3B82F6]/30 flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">
                  {s.visitor_name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-white text-sm font-semibold truncate">{s.visitor_name}</p>
                  <p className="text-[#5E7398] text-xs truncate">{s.visitor_email || "—"}</p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                {statusBadge(s)}
                {s.unread_admin > 0 && (
                  <span className="w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center font-bold animate-pulse">
                    {s.unread_admin}
                  </span>
                )}
              </div>
            </div>
            {s.last_message && <p className="text-[#7B8FAF] text-xs truncate mb-2">{s.last_message}</p>}
            <div className="flex items-center justify-between gap-2">
              <span className="text-[#465886] text-xs">{relTime(s.updated_at)}</span>
              <div className="flex gap-1.5">
                {s.status === "waiting" && (
                  <button onClick={e => { e.stopPropagation(); handleAssign(s.id); }}
                    className="text-xs px-2 py-1 bg-[#3B82F6] text-white rounded-lg hover:bg-[#3d4e6a] cursor-pointer whitespace-nowrap transition-colors">
                    Ustlen
                  </button>
                )}
                <button onClick={e => { e.stopPropagation(); handleClose(s.id); }} disabled={closingId === s.id}
                  className="text-xs px-2 py-1 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/10 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50">
                  {closingId === s.id ? <i className="ri-loader-4-line animate-spin"></i> : "Kapat"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="lg:col-span-3 bg-[#141E35] border border-[#2B3654] rounded-xl flex flex-col overflow-hidden">
        {selected && sessionInfo ? (
          <>
            <div className="px-4 py-3 border-b border-[#2B3654] flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-[#3B82F6]/30 flex items-center justify-center text-white font-bold flex-shrink-0">
                  {sessionInfo.visitor_name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-white text-sm font-semibold truncate">{sessionInfo.visitor_name}</p>
                  <p className="text-[#5E7398] text-xs">
                    {sessionInfo.visitor_email} {sessionInfo.visitor_phone ? `· ${sessionInfo.visitor_phone}` : ""}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {statusBadge(sessionInfo)}
                {sessionInfo.assigned_to && <span className="text-xs text-[#7B8FAF]">{sessionInfo.assigned_to}</span>}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#0F1729]">
              {loadingMsgs && messages.length === 0 ? (
                <div className="flex justify-center pt-10"><i className="ri-loader-4-line animate-spin text-[#3B82F6] text-2xl"></i></div>
              ) : messages.map((msg, i) => (
                <div key={`${msg.id}-${i}`} className={`flex ${msg.sender === "agent" ? "justify-end" : "justify-start"} gap-2`}>
                  {msg.sender === "visitor" && (
                    <div className="w-7 h-7 rounded-full bg-[#3B82F6]/40 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-1">
                      {sessionInfo.visitor_name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className={`max-w-[72%] flex flex-col gap-1 ${msg.sender === "agent" ? "items-end" : "items-start"}`}>
                    {msg.sender === "agent" && msg.agent_name && (
                      <span className="text-[#7B8FAF] text-xs">{msg.agent_name}</span>
                    )}
                    <div className={`px-3 py-2 rounded-2xl text-sm leading-snug ${
                      msg.sender === "agent" ? "bg-[#3B82F6] text-white rounded-tr-sm" : "bg-[#1E2B4A] text-[#B8C5D8] rounded-tl-sm"
                    }`}>
                      {msg.message}
                    </div>
                    <span className="text-[#465886] text-xs">
                      {new Date(msg.created_at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef}></div>
            </div>
            {sessionInfo.status !== "closed" ? (
              <div className="p-3 border-t border-[#2B3654] bg-[#141E35] flex items-center gap-2">
                <input type="text" value={inputMsg}
                  onChange={e => setInputMsg(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && !e.shiftKey && handleSend()}
                  placeholder={`${agentName} olarak yanitla...`}
                  className="flex-1 bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-full px-4 py-2 outline-none focus:border-[#3B82F6] placeholder-[#5E7398]"
                />
                <button onClick={handleSend} disabled={sending || !inputMsg.trim()}
                  className="w-9 h-9 rounded-full flex items-center justify-center cursor-pointer bg-[#3B82F6] hover:bg-[#3d4e6a] transition-colors flex-shrink-0 disabled:opacity-50">
                  <i className={sending ? "ri-loader-4-line animate-spin text-white" : "ri-send-plane-fill text-white text-sm"}></i>
                </button>
              </div>
            ) : (
              <div className="p-3 border-t border-[#2B3654] text-center">
                <p className="text-[#5E7398] text-xs">Bu sohbet kapatildi</p>
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-10">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-[#0F1729] rounded-xl">
              <i className="ri-chat-3-line text-3xl text-[#3B82F6]"></i>
            </div>
            <p className="text-white font-medium">Konusma secin</p>
            <p className="text-[#7B8FAF] text-sm mt-1">Soldan bir musteriy secin</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════
// Gecmis Konusmalar (Gruplandirilmis)
// ════════════════════════════════════════════

type CloseReasonFilter = "all" | "visitor" | "timeout" | "admin";

function ClosedChatsPanel() {
  const [filter, setFilter] = useState<CloseReasonFilter>("all");
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sessionInfo, setSessionInfo] = useState<SessionRow | null>(null);

  const load = useCallback(async (reason: string) => {
    setLoading(true);
    try {
      const data = await adminGetClosedSessions(reason === "all" ? undefined : reason);
      setSessions(data);
    } catch { /* hata */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);

  const openSession = async (s: SessionRow) => {
    setSelected(s.id);
    setSessionInfo(s);
    setLoadingMsgs(true);
    try {
      const res = await adminGetSessionMessages(s.id);
      setMessages(res.messages);
    } catch { /* hata */ }
    setLoadingMsgs(false);
  };

  const reasonBadge = (r: string | null) => {
    if (r === "visitor") return { label: "Musteri kapatti", color: "text-green-400 bg-green-500/10 border-green-500/20" };
    if (r === "timeout") return { label: "Zaman asimi", color: "text-orange-400 bg-orange-500/10 border-orange-500/20" };
    if (r === "admin") return { label: "Yetkili kapatti", color: "text-[#7B8FAF] bg-white/5 border-white/10" };
    return { label: "Kapali", color: "text-[#7B8FAF] bg-white/5 border-white/10" };
  };

  const filterTabs: { id: CloseReasonFilter; label: string; icon: string }[] = [
    { id: "all", label: "Tumu", icon: "ri-archive-line" },
    { id: "visitor", label: "Musteri Kapatti", icon: "ri-user-2-line" },
    { id: "timeout", label: "Zaman Asimi", icon: "ri-alarm-warning-line" },
    { id: "admin", label: "Yetkili Kapatti", icon: "ri-shield-user-line" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {filterTabs.map(t => (
          <button key={t.id}
            onClick={() => { setFilter(t.id); setSelected(null); setMessages([]); setSessionInfo(null); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap border ${
              filter === t.id
                ? "bg-[#3B82F6] text-white border-[#3B82F6]"
                : "bg-[#141E35] text-[#7B8FAF] border-[#2B3654] hover:border-[#3B82F6]/40"
            }`}
          >
            <i className={`${t.icon} text-sm`}></i>
            {t.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-2 space-y-2 max-h-[calc(100vh-340px)] overflow-y-auto pr-1">
          {loading ? (
            <div className="flex justify-center py-10"><i className="ri-loader-4-line animate-spin text-2xl text-[#3B82F6]"></i></div>
          ) : sessions.length === 0 ? (
            <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-10 text-center">
              <i className="ri-archive-line text-4xl text-[#5E7398]"></i>
              <p className="text-[#7B8FAF] text-sm mt-3">Bu kategoride sohbet yok</p>
            </div>
          ) : sessions.map(s => {
            const rb = reasonBadge(s.close_reason);
            return (
              <div key={s.id} onClick={() => openSession(s)}
                className={`bg-[#141E35] border rounded-xl p-4 cursor-pointer transition-all ${
                  selected === s.id ? "border-[#3B82F6]/60 bg-[#3B82F6]/10" : "border-[#2B3654] hover:border-[#3B82F6]/30"
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-[#2B3654] flex items-center justify-center text-[#7B8FAF] font-bold text-sm flex-shrink-0">
                      {s.visitor_name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[#A5B5CC] text-sm font-medium truncate">{s.visitor_name}</p>
                      <p className="text-[#465886] text-xs">{relTime(s.updated_at)}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full border whitespace-nowrap ${rb.color}`}>{rb.label}</span>
                </div>
                {s.last_message && (
                  <p className="text-[#5E7398] text-xs truncate mt-1 ml-10">{s.last_message}</p>
                )}
              </div>
            );
          })}
        </div>
        <div className="lg:col-span-3 bg-[#141E35] border border-[#2B3654] rounded-xl flex flex-col overflow-hidden max-h-[calc(100vh-340px)]">
          {selected && sessionInfo ? (
            <>
              <div className="px-4 py-3 border-b border-[#2B3654] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#2B3654] flex items-center justify-center text-white font-bold text-sm">
                    {sessionInfo.visitor_name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold">{sessionInfo.visitor_name}</p>
                    <p className="text-[#5E7398] text-xs">{sessionInfo.visitor_email || "—"}</p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full border whitespace-nowrap ${reasonBadge(sessionInfo.close_reason).color}`}>
                  {reasonBadge(sessionInfo.close_reason).label}
                </span>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#0F1729]">
                {loadingMsgs ? (
                  <div className="flex justify-center pt-10"><i className="ri-loader-4-line animate-spin text-[#3B82F6] text-2xl"></i></div>
                ) : messages.map((msg, i) => (
                  <div key={`${msg.id}-${i}`} className={`flex ${msg.sender === "agent" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[72%] px-3 py-2 rounded-2xl text-sm ${
                      msg.sender === "agent" ? "bg-[#3B82F6]/40 text-[#B8C5D8]" : "bg-[#1E2B4A] text-[#A5B5CC]"
                    }`}>
                      {msg.message}
                      <span className="block text-xs text-[#465886] mt-1">
                        {new Date(msg.created_at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-center p-10">
              <p className="text-[#5E7398] text-sm">Gecmis konusmay goruntulemek icin secin</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════
// Musteri Veritabani
// ════════════════════════════════════════════

interface CustomersPanelProps {
  userRole: UserRole;
}

function CustomersPanel({ userRole }: CustomersPanelProps) {
  const canDelete = userRole === "admin";
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<CustomerRow | null>(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "", tags: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setCustomers(await adminGetCustomers()); } catch { /* hata */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditing(null);
    setForm({ name: "", email: "", phone: "", notes: "", tags: "" });
    setShowForm(true);
  };

  const openEdit = (c: CustomerRow) => {
    setEditing(c);
    setForm({ name: c.name, email: c.email, phone: c.phone, notes: c.notes, tags: c.tags });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name) return;
    setSaving(true);
    try {
      if (editing) await adminUpdateCustomer({ ...editing, ...form });
      else await adminAddCustomer(form);
      setShowForm(false);
      load();
    } catch { /* hata */ }
    setSaving(false);
  };

  const filtered = customers.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.email.toLowerCase().includes(search.toLowerCase()) ||
    c.phone.includes(search)
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 bg-[#0F1729] border border-[#2B3654] rounded-lg px-3 py-2 w-full sm:w-72">
          <i className="ri-search-line text-[#5E7398] text-sm"></i>
          <input type="text" placeholder="Musteri ara..." value={search}
            onChange={e => setSearch(e.target.value)}
            className="bg-transparent text-white text-sm outline-none placeholder-[#5E7398] flex-1" />
        </div>
        <button onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white text-sm rounded-lg hover:bg-[#3d4e6a] cursor-pointer whitespace-nowrap transition-colors">
          <i className="ri-user-add-line"></i> Musteri Ekle
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><i className="ri-loader-4-line animate-spin text-2xl text-[#3B82F6]"></i></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.length === 0 ? (
            <div className="col-span-full bg-[#141E35] border border-[#2B3654] rounded-xl p-10 text-center">
              <i className="ri-user-line text-4xl text-[#5E7398]"></i>
              <p className="text-[#7B8FAF] text-sm mt-3">{search ? "Sonuc bulunamadi" : "Henuz musteri kaydi yok"}</p>
            </div>
          ) : filtered.map(c => (
            <div key={c.id} className="bg-[#141E35] border border-[#2B3654] rounded-xl p-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#3B82F6]/30 flex items-center justify-center text-white font-bold flex-shrink-0">
                  {c.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-semibold truncate">{c.name}</p>
                  <p className="text-[#5E7398] text-xs truncate">{c.email || "—"}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => openEdit(c)}
                    className="w-7 h-7 flex items-center justify-center text-[#7B8FAF] hover:text-white hover:bg-[#3B82F6]/20 rounded-lg transition-colors cursor-pointer">
                    <i className="ri-edit-line text-sm"></i>
                  </button>
                  {canDelete && (
                    <button
                      onClick={async () => {
                        if (!window.confirm("Bu musteriy silmek istiyor musunuz?")) return;
                        try { await import("@/utils/chatApi").then(m => m.adminDeleteCustomer(c.id)); load(); } catch { /* hata */ }
                      }}
                      className="w-7 h-7 flex items-center justify-center text-[#7B8FAF] hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  )}
                </div>
              </div>
              {c.phone && (
                <div className="flex items-center gap-2">
                  <i className="ri-phone-line text-[#5E7398] text-xs"></i>
                  <a href={`tel:${c.phone.replace(/\s/g, "")}`} className="text-[#B8C5D8] text-xs hover:text-white transition-colors">{c.phone}</a>
                </div>
              )}
              {c.notes && (
                <p className="text-[#7B8FAF] text-xs bg-[#0F1729] rounded-lg px-3 py-2 line-clamp-2">{c.notes}</p>
              )}
              {c.tags && (
                <div className="flex flex-wrap gap-1">
                  {c.tags.split(",").filter(Boolean).map((tag, ti) => (
                    <span key={ti} className="text-xs px-2 py-0.5 rounded-full bg-[#3B82F6]/20 text-[#A5B5CC] border border-[#3B82F6]/30">
                      {tag.trim()}
                    </span>
                  ))}
                </div>
              )}
              <p className="text-[#465886] text-xs">{new Date(c.created_at).toLocaleDateString("tr-TR")}</p>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="bg-[#141E35] border border-[#2B3654] rounded-2xl p-6 w-full max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-semibold">{editing ? "Musteri Duzenle" : "Yeni Musteri"}</h3>
              <button onClick={() => setShowForm(false)} className="text-[#7B8FAF] hover:text-white cursor-pointer w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/5">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            {(["name", "email", "phone"] as const).map(key => (
              <div key={key}>
                <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">
                  {key === "name" ? "Ad Soyad *" : key === "email" ? "E-posta" : "Telefon"}
                </label>
                <input
                  type={key === "email" ? "email" : key === "phone" ? "tel" : "text"}
                  value={form[key]}
                  onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                  className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-[#3B82F6]"
                />
              </div>
            ))}
            <div>
              <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Etiketler</label>
              <input type="text" value={form.tags}
                onChange={e => setForm(p => ({ ...p, tags: e.target.value }))}
                placeholder="vip, kurumsal, potansiyel"
                className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-[#3B82F6] placeholder-[#5E7398]" />
              <p className="text-[#465886] text-xs mt-1">Virgülle ayirin</p>
            </div>
            <div>
              <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Notlar</label>
              <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                rows={3} placeholder="Musteri hakkinda notlar..."
                className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-[#3B82F6] resize-none placeholder-[#5E7398]" />
            </div>
            <div className="flex gap-3 pt-1">
              <button onClick={() => setShowForm(false)}
                className="flex-1 py-2.5 border border-[#2B3654] text-[#7B8FAF] text-sm rounded-lg hover:text-white hover:border-[#3B82F6]/50 cursor-pointer whitespace-nowrap transition-colors">
                Iptal
              </button>
              <button onClick={handleSave} disabled={saving || !form.name}
                className="flex-1 py-2.5 bg-[#3B82F6] text-white text-sm rounded-lg hover:bg-[#3d4e6a] cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {saving ? <><i className="ri-loader-4-line animate-spin"></i> Kaydediliyor...</> : "Kaydet"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════
// Ana bilesen
// ════════════════════════════════════════════

type LiveTab = "active" | "closed" | "customers";

interface LiveSupportManagerProps {
  externalUnread?: number;
  onUnreadChange?: (val: number) => void;
  userRole?: UserRole;
}

export default function LiveSupportManager({ externalUnread, onUnreadChange, userRole = "editor" }: LiveSupportManagerProps) {
  const [activeTab, setActiveTab] = useState<LiveTab>("active");
  const [localUnread, setLocalUnread] = useState(0);
  const totalUnread = externalUnread !== undefined ? externalUnread : localUnread;

  useEffect(() => {
    if (externalUnread !== undefined) return;
    const tick = async () => {
      try {
        const res = await adminPollSessions();
        setLocalUnread(res.totalUnread);
        onUnreadChange?.(res.totalUnread);
      } catch { /* sessizce */ }
    };
    tick();
    const t = setInterval(tick, 5000);
    return () => clearInterval(t);
  }, [externalUnread, onUnreadChange]);

  const tabs: { id: LiveTab; label: string; icon: string }[] = [
    { id: "active", label: "Aktif Konusmalar", icon: "ri-chat-3-line" },
    { id: "closed", label: "Gecmis", icon: "ri-archive-line" },
    { id: "customers", label: "Musteriler", icon: "ri-contacts-book-2-line" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-white tracking-tight">Destek Merkezi</h1>
          {totalUnread > 0 && (
            <span className="bg-red-500 text-white text-sm px-2.5 py-0.5 rounded-full font-bold animate-pulse">
              {totalUnread} yeni
            </span>
          )}
        </div>
        <p className="text-[#7B8FAF] text-sm mt-0.5">Gercek zamanli musteri sohbetleri · otomatik yenileme</p>
      </div>

      <div className="flex gap-2 bg-[#0F1729] border border-[#2B3654] rounded-xl p-1.5">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
              activeTab === t.id ? "bg-[#3B82F6] text-white" : "text-[#7B8FAF] hover:text-white"
            }`}>
            <i className={`${t.icon} text-sm`}></i>
            <span>{t.label}</span>
            {t.id === "active" && totalUnread > 0 && (
              <span className="bg-red-500 text-white text-xs px-1.5 rounded-full">{totalUnread}</span>
            )}
          </button>
        ))}
      </div>

      {activeTab === "active" && <ActiveChatsPanel />}
      {activeTab === "closed" && <ClosedChatsPanel />}
      {activeTab === "customers" && <CustomersPanel userRole={userRole} />}
    </div>
  );
}
